import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Link, useLocation } from 'react-router-dom';
import { Star } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * iter75 — Generic Hub tabs bar with pin-to-Favorites.
 *
 * Props:
 *   tabs:      [{ key, label, path, icon, allow: Set<role>, perms?: string[] }]
 *   userRole:  user.role
 *   hasPermission?: (perm) => boolean  // iter160 — when supplied, tabs visible
 *                                       // if role matches OR any perm granted.
 *   testIdPrefix?: defaults to 'hub'
 *
 * Pinning persists per-user via PUT /api/user/favorites and dispatches the
 * 'nexflow:favorites-changed' window event so the sidebar's Favorites section refreshes.
 */
const HubTabsBar = ({ tabs, userRole, hasPermission, testIdPrefix = 'hub' }) => {
  const location = useLocation();
  const [favorites, setFavorites] = useState([]);
  const [loaded, setLoaded] = useState(false);

  const refreshFavorites = useCallback(() => {
    axios.get(`${API}/user/favorites`, { withCredentials: true })
      .then((r) => {
        setFavorites(r.data?.favorites || []);
        setLoaded(true);
      })
      .catch(() => setLoaded(true));
  }, []);

  useEffect(() => {
    refreshFavorites();
    const onChange = () => refreshFavorites();
    window.addEventListener('nexflow:favorites-changed', onChange);
    return () => window.removeEventListener('nexflow:favorites-changed', onChange);
  }, [refreshFavorites]);

  const togglePin = (path) => {
    const next = favorites.includes(path) ? favorites.filter((p) => p !== path) : [...favorites, path];
    setFavorites(next);
    axios.put(`${API}/user/favorites`, { favorites: next }, { withCredentials: true })
      .then(() => window.dispatchEvent(new Event('nexflow:favorites-changed')))
      .catch(() => { /* silent — user can retry */ });
  };

  const visible = tabs.filter((t) => {
    if (t.allow && t.allow.has(userRole)) return true;
    if (typeof hasPermission === 'function' && Array.isArray(t.perms) && t.perms.some((p) => hasPermission(p))) return true;
    return false;
  });
  if (visible.length === 0) return null;

  return (
    <div
      data-testid={`${testIdPrefix}-tabs-bar`}
      className="bg-[#141414] border border-[#262626] rounded-md p-1 flex items-center gap-1 overflow-x-auto"
    >
      {visible.map((t) => {
        const active = location.pathname === t.path || location.pathname.startsWith(t.path + '/');
        const pinned = loaded && favorites.includes(t.path);
        const Icon = t.icon;
        return (
          <div key={t.key} className="relative flex items-center">
            <Link
              to={t.path}
              data-testid={`${testIdPrefix}-tab-${t.key}`}
              data-active={active}
              title={t.label}
              className={`flex items-center gap-1.5 pl-3 pr-7 h-9 rounded text-xs font-medium whitespace-nowrap transition-colors ${
                active
                  ? 'bg-[#D4AF37] text-[#0A0A0A]'
                  : 'text-[#A3A3A3] hover:text-white hover:bg-[#1f1f1f]'
              }`}
            >
              <Icon className="w-3.5 h-3.5" /> {t.label}
            </Link>
            <button
              type="button"
              data-testid={`${testIdPrefix}-pin-${t.key}`}
              onClick={(e) => { e.preventDefault(); e.stopPropagation(); togglePin(t.path); }}
              title={pinned ? 'Unpin from Favorites' : 'Pin to Favorites'}
              className={`absolute right-1.5 p-0.5 rounded transition-colors ${
                pinned
                  ? (active ? 'text-[#0A0A0A]' : 'text-[#D4AF37]')
                  : (active ? 'text-[#0A0A0A]/40 hover:text-[#0A0A0A]' : 'text-[#525252] hover:text-[#D4AF37]')
              }`}
            >
              <Star className={`w-3 h-3 ${pinned ? 'fill-current' : ''}`} />
            </button>
          </div>
        );
      })}
    </div>
  );
};

export default HubTabsBar;
